import { Navigate, useLocation } from 'react-router-dom';

import { LANDING_PATH } from '../config/accessControl.js';

/**
 * TEMPORARY migration shims — delete once the new signed-in home page exists.
 *
 * The milestone-2 feature pages moved to src/old/milestone2, but several flows we
 * deliberately kept still navigate to their URLs. Without these entries those
 * flows would dead-end on PageNotFound, which reads as a bug rather than as a
 * removed feature. Each redirect below exists because a KEPT file points at it:
 *
 *   /ai-recommendations                "open the app" CTA + a breadcrumb
 *                                      (shell/header/PublicHeader.jsx,
 *                                       pages/public/homepage/Hero.jsx,
 *                                       pages/data-connections/RunDetailsPage.jsx)
 *   /user-dashboard/portfolio-dashboard,
 *   /ai-studio/*                       saved "default start page" preferences
 *                                      (utils/rbacUtils.js → resolveLandingPath)
 *   /live-ops/*, /players/*            the form→home map in DashboardHeader.jsx
 *                                      (the sidebar Recents section that also held these is gone)
 *   /data-intelligence/*               command-palette quick action
 *                                      (shell/command-palette/CommandPaletteActions.jsx)
 *   /releases/*                        avatar-popup menu rows
 *                                      (shell/header/HelpPopupButton.jsx)
 *
 * Everything else that was removed (/chat, /dashboard, /marketplace, /store) is
 * intentionally left to fall through to PageNotFound — nothing we kept links there.
 *
 * /homepage is NOT in this list: it is a real page (pages/hub/homepage/Homepage.jsx), out of the nav
 * since 26 October 2026 and reachable by URL only.
 *
 * Target: LANDING_PATH (config/accessControl.js), which every signed-in member can open; it sends them
 * on to their one game or to the games list.
 */
const to = (path) => <Navigate to={path} replace />;

/**
 * Swaps one leading path segment for another, keeping the rest of the path, the query and the hash.
 * Used for the /game-settings -> /admin-settings rename: the admin surface was renamed to match its
 * label, and every URL anyone had bookmarked, pasted in a doc, or received in an email before that
 * points at the old prefix. Without this they all 404.
 */
const PrefixRedirect = ({ from, to: target }) => {
    const { pathname, search, hash } = useLocation();
    return <Navigate to={`${target}${pathname.slice(from.length)}${search}${hash}`} replace />;
};

const LANDING = LANDING_PATH;

// The org-user and game-access-mgmt pages were folded into User Management
// (/admin-settings/user-mgmt), whose Requests tab now holds both approval queues. Unlike the
// entries above these are NOT dead ends we are papering over: notifications already sitting in
// people's inboxes carry the old paths (they are persisted on the notification row, so fixing the
// emitters does not fix the ones already sent), and their CTA is the only way an approver reaches
// the queue. Keep these until those notifications have aged out.
const USER_MGMT_REQUESTS = '/admin-settings/user-mgmt?tab=requests';

const migrationRedirects = [
    { path: '/admin-settings/access-mgmt/org-user', element: to(USER_MGMT_REQUESTS) },
    { path: '/admin-settings/access-mgmt/org-user/*', element: to(USER_MGMT_REQUESTS) },
    { path: '/admin-settings/game-mgmt/game-access-mgmt', element: to(USER_MGMT_REQUESTS) },
    { path: '/admin-settings/game-mgmt/game-access-mgmt/*', element: to(USER_MGMT_REQUESTS) },
    // The two entries above are matched by the NEW spelling; notification rows persisted before the
    // /game-settings -> /admin-settings rename carry the old one. They fall through to the prefix
    // redirect below, which rewrites them and lets the router match again on the next pass.
    //
    // Keep this LAST of the admin entries: it is a catch-all for the whole old prefix, so anything
    // needing a more specific target must be listed above it.
    { path: '/game-settings/*', element: <PrefixRedirect from="/game-settings" to="/admin-settings" /> },
    { path: '/game-settings', element: to('/admin-settings') },
    { path: '/ai-recommendations', element: to(LANDING) },
    { path: '/ai-recommendations/*', element: to(LANDING) },
    { path: '/user-dashboard/portfolio-dashboard', element: to(LANDING) },
    { path: '/ai-studio/*', element: to(LANDING) },
    { path: '/live-ops/*', element: to(LANDING) },
    { path: '/players/*', element: to(LANDING) },
    { path: '/data-intelligence/*', element: to(LANDING) },
    { path: '/releases/*', element: to(LANDING) },
];

export default migrationRedirects;
